<template>
  <ToolShell
    tool-name="Invoice generator"
    h1="Free invoice generator — create an invoice online in seconds."
    lede="An invoice generator fills in a ready-made invoice for you: type your business and client details and line items, and it works out the subtotal, tax and total, then saves the invoice as a PDF. No signup, no account, no watermark."
    :trust="['No signup', 'No watermark', 'Print-ready']"
    :upsell-points="[
      'Every invoice stays attached to the client record',
      'Payment status and reminders connected, with expenses from Specialist',
      'Reporting shows what each client is worth',
    ]"
    :free-bullets="[
      'A printable invoice you build one at a time',
      'You re-enter the same clients on every invoice',
      'Payment tracking happens in your head or a spreadsheet',
      'Follow-up reminders are manual, one email at a time',
    ]"
    :connected-bullets="[
      'Clients approve the proposal online, from the link',
      'Approved proposals convert to invoices in one click',
      'Payment links collect online; payment plans start on Specialist',
      'Follow-up reminders run on a schedule with automations',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/invoice-generator"
    :meta-description="metaDescription"
    :doc-brand="form.businessName || 'Your business'"
    :doc-subtitle="form.businessEmail"
    doc-kind="INVOICE"
    stamp="Preview"
  >
    <template #form>
      <label class="ta-field">
        <span>Business name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14V4.5L8 2l6 2.5V14M5.5 14v-4h5v4M2 14h12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.trim="form.businessName" type="text" placeholder="Apex Auto Spa" autocomplete="organization" />
        </div>
      </label>

      <label class="ta-field">
        <span>Email</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3.5" width="12" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="m3 5 5 3.5L13 5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.trim="form.businessEmail" type="email" placeholder="billing@yourbusiness.com" autocomplete="email" />
        </div>
      </label>

      <label class="ta-field">
        <span>Client name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.2" r="2.7" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.8 14c.7-2.6 2.7-4 5.2-4s4.5 1.4 5.2 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.trim="form.clientName" type="text" placeholder="Client or company name" />
        </div>
      </label>

      <label class="ta-field">
        <span>Client email</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3.5" width="12" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="m3 5 5 3.5L13 5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.trim="form.clientEmail" type="email" placeholder="client@company.com" />
        </div>
      </label>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Invoice #</span>
          <div class="ta-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 2.5 4 13.5M12 2.5 10.5 13.5M3 5.5h10.5M2.5 10.5H13" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            <input v-model.trim="form.invoiceNumber" type="text" />
          </div>
        </label>
        <label class="ta-field">
          <span>Tax rate %</span>
          <div class="ta-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
            <input v-model.number="form.taxRate" type="number" min="0" step="0.1" />
          </div>
        </label>
      </div>

      <div class="ta-field-row">
        <div class="ta-field">
          <span id="invoice-issued-label">Issued</span>
          <ToolDropdown
            v-model="issueOffset"
            :options="issueOptions"
            aria-label="Issue date"
            labelledby="invoice-issued-label"
          />
        </div>
        <div class="ta-field">
          <span id="invoice-due-label">Due</span>
          <ToolDropdown
            v-model="dueOffset"
            :options="dueOptions"
            aria-label="Due date"
            labelledby="invoice-due-label"
          />
        </div>
      </div>

      <div class="ta-field">
        <span>Line items</span>
        <div class="ta-lines">
          <div v-for="(item, index) in form.items" :key="index" class="ta-line">
            <input v-model.trim="item.description" type="text" placeholder="Service or product" />
            <input v-model.number="item.qty" type="number" min="0" step="1" aria-label="Quantity" />
            <input v-model.number="item.rate" type="number" min="0" step="0.01" aria-label="Rate" />
            <button type="button" class="ta-line-remove" :aria-label="`Remove item ${index + 1}`" @click="removeItem(index)">×</button>
          </div>
        </div>
        <button type="button" class="ta-add" @click="addItem">+ Add line item</button>
      </div>

      <label class="ta-field">
        <span>Notes</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5 4 10 11 3l2 2-7 7-3 .5zM9.5 4.5l2 2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.trim="form.notes" type="text" placeholder="Payment terms, thank-you note…" />
        </div>
      </label>

      <button type="button" class="ta-download" @click="printInvoice">
        Download / print
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2v9m0 0 4-4m-4 4L4 7M2.5 13.5h11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>

      <p class="ta-note">
        Choose "Save as PDF" in the print dialog — only the invoice document
        prints, the page around it is removed automatically.
      </p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Invoice #</span><strong>{{ form.invoiceNumber || "—" }}</strong></div>
        <div><span>Issued</span><strong>{{ formatDate(issueDate) }}</strong></div>
        <div><span>Due</span><strong>{{ formatDate(dueDate) }}</strong></div>
        <div><span>Bill to</span><strong>{{ form.clientName || "Client name" }}</strong></div>
        <div v-if="form.clientEmail"><span>Client email</span><strong>{{ form.clientEmail }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in form.items" :key="index">
              <td>{{ item.description || "—" }}</td>
              <td class="ta-num">{{ item.qty }}</td>
              <td class="ta-num">{{ money(item.rate) }}</td>
              <td class="ta-num">{{ money(item.qty * item.rate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-subtotals">
        <div><span>Subtotal</span><span>{{ money(subtotal) }}</span></div>
        <div v-if="form.taxRate > 0"><span>Tax ({{ form.taxRate }}%)</span><span>{{ money(taxAmount) }}</span></div>
      </div>

      <div class="ta-doc-total" :class="{ 'ta-doc-total--long': money(total).length > 11 }">
        <span>Total due</span>
        <strong>{{ money(total) }}</strong>
      </div>

      <p v-if="form.notes" class="ta-doc-note">{{ form.notes }}</p>
      <div v-else class="doc-end-pad" aria-hidden="true"></div>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Add your details</h2><p>Business and client names and emails, then pick the issue and due dates.</p></div></div>
      <div><span>02</span><div><h2>Fill in line items</h2><p>Quantity and rate per line — subtotal, tax, and the total calculate automatically.</p></div></div>
      <div><span>03</span><div><h2>Download or print</h2><p>Only the invoice document prints — save it as a PDF and send it to your client.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Invoice generator FAQ" :faq="faq" />

    <RelatedPages path="/tools/invoice-generator" title="Free invoice generator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const form = reactive({
  businessName: "",
  businessEmail: "",
  clientName: "",
  clientEmail: "",
  invoiceNumber: "",
  items: [
    { description: "", qty: 1, rate: 0 },
    { description: "", qty: 1, rate: 0 },
    { description: "", qty: 1, rate: 0 },
  ],
  taxRate: 0,
  notes: "",
});

const issueOffset = ref(0);
const dueOffset = ref(14);

// Dates are client-only: prerendered HTML must stay identical day to day or
// the localization pipeline fails with "Rendered site copy changed".
const now = ref(null);
onMounted(() => {
  now.value = new Date();
  form.invoiceNumber = `${String(now.value.getFullYear()).slice(2)}-1`;
});

const dateForOffset = (offset) => {
  if (!now.value) return "";
  const d = new Date(now.value);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

const hintForOffset = (offset) => {
  const iso = dateForOffset(offset);
  return iso ? formatDate(iso) : "";
};

const issueOptions = computed(() =>
  [
    { value: 0, label: "Today" },
    { value: -1, label: "Yesterday" },
    { value: -7, label: "7 days ago" },
    { value: -30, label: "30 days ago" },
  ].map((option) => ({ ...option, hint: hintForOffset(option.value) })),
);

const dueOptions = computed(() =>
  [
    { value: 7, label: "In 7 days" },
    { value: 14, label: "In 14 days" },
    { value: 30, label: "In 30 days" },
    { value: 60, label: "In 60 days" },
  ].map((option) => ({ ...option, hint: hintForOffset(option.value) })),
);

const issueDate = computed(() => dateForOffset(issueOffset.value));
const dueDate = computed(() => dateForOffset(dueOffset.value));

const subtotal = computed(() =>
  form.items.reduce((sum, item) => sum + (Number(item.qty) || 0) * (Number(item.rate) || 0), 0),
);
const taxAmount = computed(() => subtotal.value * ((Number(form.taxRate) || 0) / 100));
const total = computed(() => subtotal.value + taxAmount.value);

const money = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const addItem = () => form.items.push({ description: "", qty: 1, rate: 0 });
const removeItem = (index) => {
  if (form.items.length > 1) form.items.splice(index, 1);
};

const printInvoice = () => window.print();

const guideSections = [
  {
    title: "Filling in the generator, field by field",
    paragraphs: [
      "Every field above lands in a fixed place on the invoice preview, so you can see the finished document as you type.",
    ],
    bullets: [
      "Business name and email: the header of the invoice, so the client knows who is billing them.",
      "Client name and email: the Bill to and Client email lines. Use the legal or company name the client's accounts team will recognise.",
      "Invoice #: starts as the year and 1, such as 26-1. Change it to the next number in your own sequence.",
      "Tax rate %: applied once to the subtotal of every line. Leave it at 0 if you do not charge tax.",
      "Issued: today, yesterday, 7 days ago or 30 days ago, for invoices you are catching up on.",
      "Due: 7, 14, 30 or 60 days after today, with the exact date shown next to each choice.",
      "Line items: a description, quantity and rate per line. The amount is quantity times rate.",
      "Notes: payment terms, how to pay, or a short thank-you. It prints under the total.",
    ],
  },
  {
    title: "A worked example",
    paragraphs: [
      "Say a web designer bills three lines: website design, 1 at $2,400; copywriting, 6 hours at $85; and hosting setup, 1 at $150. The amounts are $2,400.00, $510.00 and $150.00, so the subtotal is $3,060.00.",
      "With an 8% tax rate, the generator adds $244.80 and shows a total due of $3,304.80. Put the payment terms and how to pay in Notes, then save it as a PDF.",
    ],
  },
  {
    title: "Saving the PDF and sending it",
    paragraphs: [
      "Click Download / print and choose Save as PDF in the print dialog. Only the invoice prints, so the PDF has no website around it.",
    ],
    bullets: [
      "Name the file with the invoice number and client, such as 26-14-acme.pdf, so both of you can find it later.",
      "Attach it to an email that repeats the amount, the due date and how to pay, so the client can act without opening it.",
      "Keep your own copy. The generator does not save anything, and closing the tab clears the form.",
      "Note the number you used, so the next invoice gets the next one.",
    ],
    links: [
      { label: "Invoice email templates", path: "/learn/invoice-email-templates" },
      { label: "What is an invoice number?", path: "/learn/what-is-an-invoice-number" },
    ],
  },
  {
    title: "Generator, template or invoicing software",
    paragraphs: [
      "All three get an invoice out. They differ in how much of the work they keep for next time.",
    ],
    bullets: [
      "This generator: a finished PDF in a couple of minutes, with the maths done for you. Best for one-off or occasional invoices.",
      "Invoice template: a spreadsheet file for Excel or Google Sheets that you keep and copy for each new invoice.",
      "Blank invoice template: a printable form to fill in by hand or on screen.",
      "Invoicing software: clients, numbers and payment status are kept for you. Worth it once you send several invoices a month and need to know who has paid.",
    ],
    links: [
      { label: "Invoice template for Excel and Sheets", path: "/tools/invoice-template" },
      { label: "Blank invoice template", path: "/tools/blank-invoice-template" },
      { label: "Invoice due date calculator", path: "/tools/invoice-due-date-calculator" },
    ],
  },
  {
    title: "Sending invoices from Workspace369",
    paragraphs: [
      "In Workspace369 each invoice is saved on the client record and numbered for you, and it carries a payment link so the client can pay online by card or ACH bank transfer through your own Stripe account. Recurring invoices, card autopay and late-payment reminders are on every plan. Several invoice layouts start on Voyager, and payment plans with tracked installments start on Specialist.",
    ],
    links: [{ label: "Invoicing features", path: "/features/invoices" }],
  },
];

const faq = [
  {
    q: "Is this invoice generator really free?",
    a: "Yes. The tool runs entirely on this page with no signup and no watermark. Create and download as many invoices as you need.",
  },
  {
    q: "How do I save the invoice as a PDF?",
    a: "Click Download / print and choose \"Save as PDF\" in your browser’s print dialog. Only the invoice document prints — the page around it is removed automatically.",
  },
  {
    q: "What should an invoice include?",
    a: "Your business details, the client, an invoice number, issue and due dates, itemized lines with quantity and rate, totals with any tax, and a clear way to pay. Our invoice writing guide covers each part in detail.",
  },
  {
    q: "How do I track whether the client paid?",
    a: "That is where a billing system helps. Workspace369 tracks invoice status, sends payment reminders automatically, and keeps every invoice attached to the client record.",
  },
  {
    q: "Does the generator save my invoices?",
    a: "No. Everything you type stays on this page and is cleared when you close the tab. Save each invoice as a PDF and keep a note of the number you used.",
  },
  {
    q: "Can I add more line items?",
    a: "Yes. Click Add line item for each extra line, and use the × button to remove one. The subtotal, tax and total update as you type.",
  },
];

const metaTitle = "Free Invoice Generator Online | Workspace369";
const metaDescription =
  "Create and download an invoice in seconds with the free Workspace369 invoice generator. No signup, no watermark — items, tax, and totals calculated online.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
}));
</script>

<style scoped>
.doc-end-pad {
  padding-bottom: 1.5rem;
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */


</style>

<style>
@media print {
  body * {
    visibility: hidden !important;
  }

  .ta-doc,
  .ta-doc * {
    visibility: visible !important;
  }

  .ta-doc {
    position: absolute;
    inset: 0 auto auto 0;
    width: 100%;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .ta-doc-stamp {
    display: none !important;
  }
}
</style>
